export function isInIframe(): boolean {
  if (typeof window === 'undefined') return false;
  return window.self !== window.top;
}

/** A message the page embedding this frame sent. Outside a frame `window.parent` is the window
 *  itself, so without the frame check the page's own messages would pass. */
export function isFromEmbeddingPage(event: MessageEvent): boolean {
  return window.parent !== window && event.source === window.parent;
}

export function isInSameOriginIframe(): boolean {
  if (!isInIframe()) return false;

  try {
    // Accessing parent origin throws if cross-origin
    return Boolean(window.top?.location.href);
  } catch {
    return false;
  }
}

export function isCrossOriginIframe(): boolean {
  return isInIframe() && !isInSameOriginIframe();
}

/** The scrollport this element sits in, whether or not it has anything to scroll right now. */
export function getScrollableParent(element: HTMLElement): HTMLElement | Window {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflow, overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll)/.test(overflow + overflowY)) return node;
  }
  return window;
}

/** `getScrollableParent`, with the window case resolved to the element that scrolls it. */
export function getScrollableElement(element: HTMLElement): HTMLElement | null {
  const parent = getScrollableParent(element);
  const scroller = parent instanceof Window ? document.scrollingElement : parent;
  return scroller instanceof HTMLElement ? scroller : null;
}

/**
 * Returns the total height of sticky elements visually stacked above the
 * element. Walks up the DOM all the way to `<body>`, checking preceding
 * siblings at each level for `position: sticky`.
 */
export function getStickyOffset(element: HTMLElement): number {
  return stickyOffset(element, 'previousElementSibling');
}

/**
 * How much of the foot of the scroll area sticky elements *below* this one already hold — the
 * composer, most of all. Anything at `bottom: 0` lands under them, covered and unclickable.
 */
export function getStickyFooterOffset(element: HTMLElement): number {
  return stickyOffset(element, 'nextElementSibling');
}

function stickyOffset(
  element: HTMLElement,
  towards: 'previousElementSibling' | 'nextElementSibling',
): number {
  let total = 0;
  let current: HTMLElement | null = element;

  while (current && current !== document.body) {
    let sibling = current[towards] as HTMLElement | null;
    while (sibling) {
      if (window.getComputedStyle(sibling).position === 'sticky') total += sibling.offsetHeight;
      sibling = sibling[towards] as HTMLElement | null;
    }
    current = current.parentElement;
  }

  return total;
}

export function scrollIntoViewIfNeeded(element: HTMLElement | null) {
  if (!element) {
    return;
  }

  requestAnimationFrame(() => {
    const rect = element.getBoundingClientRect();
    const header = document.querySelector('[data-header]') as HTMLElement | null;
    // Only a header that sits *over* the scrolling content needs room left under it. The chat's is a
    // static flex sibling above it, so the scroll area already begins below it — reserving its height
    // as well landed everything that height short of its target, showing the tail of whatever came
    // before.
    const headerOverlaysContent =
      !!header && ['sticky', 'fixed'].includes(window.getComputedStyle(header).position);
    const headerHeight = headerOverlaysContent ? header.getBoundingClientRect().height : 0;
    const stickyOffset = getStickyOffset(element);
    const topThreshold = headerHeight + stickyOffset;

    // Against the surface that scrolls, not the window: a chat's viewport ends above its
    // composer, so measuring to the window's foot calls content off-screen that is not, and
    // scrolls the thread away from whatever the guest was reading.
    const port = getScrollportBounds(getScrollableElement(element));
    const isInView =
      rect.top >= port.top + topThreshold &&
      rect.bottom <= port.bottom - getStickyFooterOffset(element);

    if (!isInView) {
      element.style.scrollMarginTop = `${topThreshold}px`;
      element.scrollIntoView({ behavior: 'auto', block: 'start' });
      element.style.scrollMarginTop = '';
    }
  });
}

const CLIPPING_OVERFLOW = new Set(['clip', 'hidden', 'auto', 'scroll']);

/**
 * The scroller's own viewport box, before anything the guest cannot see is taken off it.
 * `position: sticky` resolves its insets against this and knows nothing of a visual viewport,
 * so anything summing insets to pin a box has to measure from here.
 */
export function getScrollportBox(scroller: HTMLElement | null) {
  if (!scroller || scroller === document.scrollingElement) {
    return { top: 0, bottom: window.innerHeight };
  }
  const box = scroller.getBoundingClientRect();
  return { top: box.top, bottom: box.bottom };
}

/**
 * A scrollport box clipped to what the guest can see. A phone's browser chrome overlays the
 * layout viewport and this page cannot scroll it away — `body` is `overflow: hidden` — so the
 * scroller's own box runs a URL bar below the screen, and anything held against its foot lands
 * behind that bar. Takes the box, not the scroller, so a caller needing both edges pays for
 * one rect.
 */
export function clipToVisibleScreen(box: { top: number; bottom: number }) {
  const seen = window.visualViewport;
  if (!seen) return box;
  return {
    top: Math.max(box.top, seen.offsetTop),
    bottom: Math.min(box.bottom, seen.offsetTop + seen.height),
  };
}

/** Where a scroller's viewport sits on screen, with what the guest cannot see taken off it. */
export function getScrollportBounds(scroller: HTMLElement | null) {
  return clipToVisibleScreen(getScrollportBox(scroller));
}

/**
 * The horizontal band of `element` that is actually on screen, after every ancestor that clips.
 * A full-bleed box hangs outside the column it sits in, so its own edges say nothing about where
 * its edges are *seen* — which is what anything pinned to them has to measure from.
 */
export function visibleXBand(element: HTMLElement) {
  let left = -Infinity;
  let right = Infinity;
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    // Named rather than "anything but `visible`": an unresolved value is not a promise to
    // clip, and taking it as one hands back a band of whatever that element's box happens
    // to be — zero, for an element no one has laid out.
    if (!CLIPPING_OVERFLOW.has(getComputedStyle(node).overflowX)) continue;
    const box = node.getBoundingClientRect();
    left = Math.max(left, box.left);
    right = Math.min(right, box.right);
  }
  return { left, right };
}

export function scrollToElementInParent(element: HTMLElement | null, offset = 16) {
  if (!element) return;

  requestAnimationFrame(() => {
    const scrollableParent = getScrollableParent(element);

    if (!(scrollableParent instanceof HTMLElement)) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const elementRect = element.getBoundingClientRect();
    const parentRect = scrollableParent.getBoundingClientRect();
    const elementTop = elementRect.top - parentRect.top + scrollableParent.scrollTop;

    scrollableParent.scrollTo({
      top: elementTop - offset,
      behavior: 'smooth',
    });
  });
}

/** How far a pointer may travel and still have been a tap, in CSS px. */
const TAP_SLOP = 8;

/** Where each press landed, kept against the control rather than in the closure that made these
 *  handlers: `tapProps(fn)` is called in render, so anything re-rendering between the press and
 *  the lift — the thread streaming a message, most of all — would hand out a fresh closure and
 *  lose the press. Keyed by pointer inside that, because a pinch puts a second finger on a card
 *  the first is already resting on, and one origin per control would measure the wrong one. */
const pressedAt = new WeakMap<EventTarget, Map<number, { x: number; y: number }>>();

/**
 * Handlers for a control that should act on the lift of a tap.
 *
 * The press is too eager — a scroll that happens to start on the control would fire it under the
 * guest's finger. The click is too late: it waits for the press to end *and* for the browser to
 * decide the gesture was a tap, and on a big layout change the wait shows.
 *
 * So the lift, with the travel measured rather than inferred. A gesture that becomes a native
 * scroll never reaches the lift — the browser cancels the pointer instead — but that only covers
 * scrollers the browser owns. Where a carousel drags itself (Swiper off `cssMode`, which is how
 * it runs with a mouse) its own defence is a capture-phase `click` listener, which a lift never
 * reaches; and a swipe at a scroller's end scrolls nothing, so nothing is cancelled either.
 *
 * The `click` below is only the keyboard's, which reports no clicks of a pointer.
 */
export function tapProps(onTap: () => void) {
  type Pointer = {
    currentTarget: EventTarget;
    pointerId: number;
    isPrimary: boolean;
    clientX: number;
    clientY: number;
  };
  return {
    onPointerDown: (event: Pointer) => {
      if (!event.isPrimary) return;
      const presses = pressedAt.get(event.currentTarget) ?? new Map();
      presses.set(event.pointerId, { x: event.clientX, y: event.clientY });
      pressedAt.set(event.currentTarget, presses);
    },
    onPointerUp: (event: Pointer & { button: number }) => {
      const from = pressedAt.get(event.currentTarget)?.get(event.pointerId);
      pressedAt.get(event.currentTarget)?.delete(event.pointerId);
      if (event.button !== 0 || !event.isPrimary || !from) return;
      if (Math.hypot(event.clientX - from.x, event.clientY - from.y) <= TAP_SLOP) onTap();
    },
    onClick: (event: { detail: number }) => {
      if (event.detail === 0) onTap();
    },
  };
}

/** Focuses an element that is not a tab stop, such as a heading, for as long as it holds focus,
 *  so it never becomes one. */
export function focusInPlace(element: HTMLElement | null | undefined) {
  if (!element) return;
  if (!element.hasAttribute('tabindex')) {
    element.setAttribute('tabindex', '-1');
    element.addEventListener('blur', () => element.removeAttribute('tabindex'), { once: true });
  }
  element.focus({ preventScroll: true });
}

/** Nothing holds focus: a focused control was removed or disabled, or nothing was ever focused. */
export function isFocusDropped(): boolean {
  return document.activeElement === null || document.activeElement === document.body;
}
